import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { PROMPTS } from "./homeData";

const ease = [0.2, 0.8, 0.2, 1];

// The live AI moment: a question is typed, TRAIDE answers in one line, then shows what it found. Cycles quietly.
export function useAiCycle(active = true, prompts = PROMPTS) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  const [typed, setTyped] = useState(reduce ? prompts[0].q : "");
  const [phase, setPhase] = useState(reduce ? "lines" : "typing"); // typing → thinking → answer → lines → hold
  useEffect(() => {
    if (!active || reduce) return;
    let alive = true;
    const p = prompts[i % prompts.length];
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(() => alive && fn(), ms));
    setTyped(""); setPhase("typing");
    p.q.split("").forEach((_, n) => later(() => setTyped(p.q.slice(0, n + 1)), 320 + n * 34));
    const done = 320 + p.q.length * 34;
    later(() => setPhase("thinking"), done + 200);
    later(() => setPhase("answer"), done + 1100);
    later(() => setPhase("lines"), done + 1600);
    later(() => setI((x) => x + 1), done + 1600 + 900 * p.lines.length + 2600);
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [i, active, reduce, prompts]);
  const p = prompts[i % prompts.length];
  return { p, typed, phase, index: i % prompts.length };
}

export default function AiPrompt({ active = true, compact = false, className = "" }) {
  const { p, typed, phase, index } = useAiCycle(active);
  const showAnswer = phase === "answer" || phase === "lines";
  return (
    <div className={`ai ${compact ? "p-3.5" : "p-4"} ${className}`} data-testid="hero-ai-prompt" data-prompt-index={index} data-phase={phase}>
      <div className="flex items-center gap-2.5">
        <span className="ai-icon"><Sparkles /></span>
        <span className="min-w-0 flex-1 truncate text-[13.5px] text-white" data-testid="ai-typed"><span className="text-zinc-500">Ask TRAIDE · </span>{typed}{phase === "typing" && <span className="ai-cursor" />}</span>
        {phase === "thinking" && <span className="ai-think" data-testid="ai-thinking"><i /><i /><i /></span>}
      </div>
      <AnimatePresence mode="wait">
        {showAnswer && (
          <motion.div key={index} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.35, ease }} className="mt-3 border-t border-white/[.08] pt-3">
            <div className="text-[13.5px] font-semibold text-white" data-testid="ai-answer">{p.a}</div>
            {phase === "lines" && (
              <ul className="mt-2 space-y-1.5">
                {p.lines.map((l, n) => (
                  <motion.li key={l} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.25 + n * 0.55, ease }} className="flex items-start gap-2.5 text-[12.5px] text-zinc-300" data-testid={`ai-line-${n}`}>
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-amber-400" />{l}
                  </motion.li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
